'use client';

import { useRouter } from '@bprogress/next/app';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  catalogService,
  cartService,
  CartFragment,
  CartItemFragment,
  getFragmentData,
  ProductFragmentDoc,
  ProductFragment
} from '@/fo/api/magento';
import { FeatureCartPageBase } from '@/fo/feature/cart/page/base';
import { storeProduct } from '@/fo/store/product';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import { ProductsMindbox } from '@/fo/util/map-product-data';

import { styles } from './FeatureCartModalCart.styles';

export interface FeatureCartModalCartProps {
  isAuthorized: boolean;
}

export function FeatureCartModalCart({
  isAuthorized
}: FeatureCartModalCartProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [cartState, setCartState] = useState<CartFragment | null>(null);
  const [itemsState, setItemsState] = useState<CartItemFragment[]>([]);
  const [mindboxProductsState, setMindboxProductsState] =
    useState<ProductsMindbox | null>(null);
  const itemsStateRef = useRef<CartItemFragment[]>([]);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const slots = styles();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathnameRef = useRef(pathname ?? '');
  const shouldAutoOpenCartDrawer = searchParams.get('openCart') === '1';
  const applyCartState = useCallback(
    (nextCart: CartFragment | null, nextItems: CartItemFragment[]) => {
      const normalizedItems = nextItems.filter(
        (item) => (item?.quantity ?? 1) > 0
      );

      itemsStateRef.current = normalizedItems;
      setCartState(nextCart);
      setItemsState(normalizedItems);

      storeProduct.send({ type: 'setClearedCart' });
      normalizedItems.forEach((item) => {
        const sku =
          'sku' in (item?.product ?? {})
            ? (item.product as ProductFragment).sku
            : undefined;
        if (!sku) return;

        storeProduct.send({
          type: 'setProductCart',
          sku,
          cart: {
            id: item?.id ?? '',
            count: item?.quantity ?? 1,
            loading: false,
            isGift: item?.is_cart_rule_gift ?? false
          }
        });
      });
    },
    []
  );

  const hideGuestCheckout =
    process.env.NEXT_PUBLIC_HIDE_GUEST_CHECKOUT === 'true';

  const refreshCart = useCallback(
    async ({ showLoader = false }: { showLoader?: boolean } = {}) => {
      const requestId = ++requestIdRef.current;

      if (showLoader) {
        setIsCartLoading(true);
      }

      try {
        const cartData = await cartService.getCart();

        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return;
        }

        applyCartState(cartData.cart, cartData.items ?? []);
      } catch {
        // ignore
      } finally {
        if (
          showLoader &&
          isMountedRef.current &&
          requestId === requestIdRef.current
        ) {
          setIsCartLoading(false);
        }
      }
    },
    [applyCartState]
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      return;
    }

    requestIdRef.current += 1;
    setIsCartLoading(false);
    setCartState(null);
    setItemsState([]);
    setMindboxProductsState(null);
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const cartProducts = itemsState
      .map((item) => getFragmentData(ProductFragmentDoc, item.product))
      .filter((product): product is ProductFragment => product !== null);

    if (!cartProducts.length) {
      setMindboxProductsState(null);
      return;
    }

    void catalogService
      .getPrices(cartProducts)
      .then((products) => setMindboxProductsState(products))
      .catch(() => setMindboxProductsState(null));
  }, [itemsState]);

  useEffect(() => {
    const unsubscribeOnOpen = utilEventCartModal.onOpen(() => {
      if (pathnameRef.current.startsWith('/checkout')) return;
      handleOpenChange(true);
    });

    const unsubscribeOnClose = utilEventCartModal.onClose(() =>
      handleOpenChange(false)
    );
    const unsubscribeOnMutate = utilEventCartModal.onMutate(() => {
      if (!open) return;
      if (pathnameRef.current.startsWith('/checkout')) return;
      void refreshCart();
    });

    return () => {
      unsubscribeOnOpen();
      unsubscribeOnClose();
      unsubscribeOnMutate();
    };
  }, [handleOpenChange, open, refreshCart]);

  useEffect(() => {
    itemsStateRef.current = itemsState;
  }, [itemsState]);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (pathname?.startsWith('/checkout')) {
      handleOpenChange(false);
      return;
    }

    if (shouldAutoOpenCartDrawer) {
      handleOpenChange(true);
    }
  }, [pathname, shouldAutoOpenCartDrawer, handleOpenChange]);

  useEffect(() => {
    if (!shouldAutoOpenCartDrawer) return;
    if (!open) return;

    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('openCart');

    const nextQuery = nextSearchParams.toString();
    router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname);
  }, [open, pathname, router, searchParams, shouldAutoOpenCartDrawer]);

  useEffect(() => {
    if (!open) return;

    void refreshCart({ showLoader: true });
  }, [open, refreshCart]);

  return (
    <UiKitDrawerBase
      open={open}
      title={t('cart.title')}
      direction="right"
      onOpenChange={handleOpenChange}
      contentClassName={slots.base()}
    >
      <FeatureCartPageBase
        hideGuestCheckout={hideGuestCheckout}
        isAuthorized={isAuthorized}
        cart={cartState}
        items={itemsState}
        mindboxProducts={mindboxProductsState}
        isCartLoading={isCartLoading}
      />
    </UiKitDrawerBase>
  );
}
